import { appQueryClient } from "app/AppQueryClient";
import {
  contextFeatureFlagRetrieve,
  getContextFeatureFlagRetrieveQueryKey,
  useContextFeatureFlagRetrieveSuspense,
} from "gen/endpoints/feature-flags/feature-flags";
import { get } from "lodash-es";

type FeatureFlagName =
  | "backdated_investor_transfers"
  | "fund_family_setup"
  | "credit_facilities"
  | "eventing_v2"
  | "hybrid_waterfalls"
  | "adjust_waterfall"
  | "cancel_correct_v0"
  | "cancel_correct_v1"
  | "financial_reporting"
  | "credit_facility_expenses"
  | "credit_facility_merge_tranches"
  | "display_time_weighted_returns"
  | "performance_metrics"
  | "credit_facility_reallocation_via_capital_calls"
  | "use_versioning_on_security_ownership_tables"
  | "cap_call_reallocations"
  | "frontend_adjustments"
  | "fee_basis_tab";

// We want to cache feature flags but have caching turned off by default.
// We set them specifically for this request.
// https://tanstack.com/query/v4/docs/react/guides/caching
const STALE_TIME = 1 * 60 * 1000; // 1 minute
const CACHE_TIME = 5 * 60 * 1000; // 5 minutes

export const useFeatureFlagActive = (flagName: FeatureFlagName): boolean => {
  const { data } = useContextFeatureFlagRetrieveSuspense({
    query: {
      staleTime: STALE_TIME,
      gcTime: CACHE_TIME,
    },
  });
  return get(data.feature_flags, flagName, false);
};

export const isFeatureFlagActive = async (
  flagName: FeatureFlagName,
): Promise<boolean> => {
  const data = await appQueryClient.ensureQueryData({
    queryKey: getContextFeatureFlagRetrieveQueryKey(),
    queryFn: () => contextFeatureFlagRetrieve(),
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
  });
  return get(data.feature_flags, flagName, false);
};
